import React, { Dispatch, SetStateAction } from 'react';
import { useRouter } from 'next/router';
import styled, { css, useTheme } from 'styled-components';
import { Box, Button, Icon, Pill, Text } from '@sparknz/spark-pattern-library';
import { trackCustomLink } from '@sparknz/tracking';

import { useProductDetails } from 'journey/productDetails/provider';
import { ProductDetailsActionTypes } from 'journey/productDetails/reducer/productDetails.reducer';
import { InfoLayerStatus } from 'journey/productDetails/typings';
import { trackLinkedProductsEvent } from 'journey/productDetails/utils';
import Clickable from 'shared/components/Clickable';
import { useSegment } from 'shared/hooks';
import { extractFirstValue } from 'shared/utils';
import { getClickAndCollectInfo } from '../../utils/getClickToCollectMessage.utils';

type ClickAndCollectSectionProps = {
  setShowFindStore: Dispatch<SetStateAction<boolean>>;
};

export const InlineIcon = styled(Icon)`
  ${({ theme: { spacing } }) => css`
    position: relative;
    align-self: start;
    margin-right: ${spacing.s1};
  `}
`;

export const DeliveryOptionsInformationMiddleSection = styled.div`
  max-width: 170px;
`;

const ClickToCollectSection = ({
  setShowFindStore,
}: ClickAndCollectSectionProps) => {
  const router = useRouter();
  const {
    dispatch,
    planOffers: planOffersDetails,
    ifpEligibility: { isChangePlan },
    selectedDevice,
    paymentOptions,
    changePlanOffers,
  } = useProductDetails();
  const { palette } = useTheme();
  const { isBusiness } = useSegment();
  const { stock, isClickToCollectEnabled } = selectedDevice;
  const isSelectedDeviceAvailableForStore = stock?.storeStatus === 'IN_STOCK';
  const ifpId = extractFirstValue(router.query.ifpId);
  const planId = extractFirstValue(router.query.planId);
  const planOffers = changePlanOffers || planOffersDetails;
  const shouldShowInfoIcon = isClickToCollectEnabled !== false;
  const { title } = getClickAndCollectInfo(
    isClickToCollectEnabled,
    isSelectedDeviceAvailableForStore,
  );

  const handleDisplayFindStore = () => {
    setShowFindStore(true);
    trackCustomLink('Find your nearest store');
    trackLinkedProductsEvent({
      eventInfo: {
        eventName: 'Click & Collect Find Your Nearest Store Action Layer',
        eventAction: 'actionLayer.view',
      },
      device: selectedDevice,
      isBusiness,
      plan: planOffers?.find(({ id }) => id === planId),
      paymentOption: paymentOptions?.find(({ id }) => id === ifpId),
      isChangePlan,
    });
  };
  const handleClick = (contentKey: string, customLink: string) => {
    dispatch({
      type: ProductDetailsActionTypes.SetInfoLayer,
      infoLayer: {
        contentKey,
        status: InfoLayerStatus.RawHtml,
        isNested: false,
      },
    });
    trackCustomLink(customLink);
  };

  return (
    <Box flexDirection="row" justifyContent="space-between">
      <Box flexDirection="row">
        <InlineIcon name="pin_small" />
        <DeliveryOptionsInformationMiddleSection>
          <Text variant="bodySMedium" mb="s1">
            {title}
            {shouldShowInfoIcon ? (
              <Clickable
                aria-label="Click & Collect available Information icon"
                title="Click & Collect available Information icon"
                type="button"
                onClick={() => {
                  handleClick(
                    'productDetails.clickAndCollect',
                    'Click & Collect Information icon',
                  );
                }}
              >
                <Icon name="info_small" fill={palette.baseColors.lupin} />
              </Clickable>
            ) : null}
          </Text>
          {isSelectedDeviceAvailableForStore ? (
            <Button
              variant="textbutton"
              type="button"
              onClick={handleDisplayFindStore}
            >
              Find your nearest store
            </Button>
          ) : null}
        </DeliveryOptionsInformationMiddleSection>
      </Box>
      <div>
        {isSelectedDeviceAvailableForStore ? (
          <Pill text="In stock" color="sparkGreen" />
        ) : (
          <Pill text="Out of stock" color="moeraki" />
        )}
      </div>
    </Box>
  );
};

export default ClickToCollectSection;
